import { Colors, Icon } from "swing-components";

import { Button } from "~components";
import { StepComputed, UnsyncedSkillBuilderAttribute } from "~skillBuilder/utils";
import { msg } from "~utils";

import { Question } from "../Question";

type StepProps = {
  stepComputed: Omit<StepComputed, "prevButtonText" | "nextButtonText">;
  /**
   * When answering a Question, this function will pass the `value` to the
   * parent TaskPage.
   */
  onChange: ({ attribute, value }: UnsyncedSkillBuilderAttribute) => void;
  /**
   * Disabled the ability to answer any questions.
   * @default false
   */
  isReadOnly?: boolean;
  /**
   * Helps calculate the correct "Next" button label
   * @default false
   */
  isLastStep?: boolean;
  /**
   * Button props to for next button.
   */
  nextButtonProps: {
    label?: string;
    onClick: () => void;
    /**
     * Shows a loading spinner on the button
     * @default false
     */
    isLoading?: boolean;
    /**
     * @default false
     */
    disabled?: boolean;
  };
  /**
   * Button props to for prev button.
   */
  prevButtonProps?: {
    label?: string;
    onClick: () => void;
    /**
     * @default false
     */
    disabled?: boolean;
    // NOTE: This button cannot be in a loading state.
  };
};

export function Step(props: StepProps) {
  const {
    stepComputed,
    prevButtonProps,
    nextButtonProps,
    onChange,
    isReadOnly = false,
    isLastStep = false,
  } = props;
  const { status, questions, header } = stepComputed;

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        gap: 40,
      }}
    >
      {/* Header */}
      {header && (
        <header css={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <h2
            css={{
              fontSize: 20,
              lineHeight: "30px",
              fontWeight: 600,
              margin: 0,
              color: Colors.black,
            }}
          >
            {header.title}
          </h2>

          {/* List of icon and label */}
          {header.decorations && (
            <ul
              css={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                paddingLeft: 24,
                margin: 0,
              }}
            >
              {header.decorations.map(({ icon, label }) => (
                <li key={`${header.title}-${label}`} css={{ display: "flex" }}>
                  <Icon name={icon} />
                  <span
                    css={{
                      fontSize: 16,
                      lineHeight: "24px",
                      fontWeight: 400,
                      color: Colors.black,
                    }}
                  >
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* Divider */}
          <hr css={{ width: "100%", background: Colors.slate300, margin: 0 }} />
        </header>
      )}

      {/* Questions */}
      {questions.map((question) => (
        <Question
          key={question.attribute}
          {...question}
          value={question.value}
          onChange={onChange}
          isReadOnly={isReadOnly}
        />
      ))}

      {/* Buttons */}
      <div
        css={{
          display: "grid",
          gap: 8,
          // Stacked layout on mobile
          gridTemplateColumns: "1fr",
          // side-by-side layout on desktop
          // breakpoint provided by design team
          // TODO: Update this to use the Breakpoints constant
          "@media (min-width: 415px)": {
            // Rendering a double column when there is two buttons
            ...(prevButtonProps && {
              gridTemplateColumns: "auto 1fr",
            }),
          },
        }}
      >
        {/* Next Button */}
        <div
          css={{
            justifySelf: "stretch",
            // TODO: Update this to use the Breakpoints constant
            "@media (min-width: 415px)": {
              justifySelf: "flex-end",
              // set the order to the last item
              // on tablet/desktop
              order: 2,
            },
          }}
        >
          <Button
            data-testid="step-next-button"
            onClick={nextButtonProps.onClick}
            label={getNextButtonLabel({
              label: nextButtonProps?.label,
              stepStatus: status,
              isLastStep,
              isReadOnly,
            })}
            disabled={nextButtonProps.disabled}
            submitted={nextButtonProps.isLoading ?? false}
            fill="solid"
          />
        </div>

        {/* Previous Button */}
        {prevButtonProps && (
          <div
            css={{
              justifySelf: "stretch",
              // TODO: Update this to use the Breakpoints constant
              "@media (min-width: 415px)": {
                // set the order to the first item
                // on tablet/desktop
                order: 1,
              },
            }}
          >
            <Button
              onClick={prevButtonProps.onClick}
              // Either the given label or "Previous"
              label={prevButtonProps.label ?? msg("LABEL_PREVIOUS")}
              disabled={prevButtonProps.disabled}
              fill="outline"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/********** Helper Functions *********/
/**
 * Returns the appropriate next button label following these rules:
 * 1. If on the last step and NOT in a read-only state, return either the given label or "Submit"
 *   since this can be customized in the Step configuration.
 * 2. When on the last step and IN a read-only state, return "Return Home" since the user cannot move
 * forward nor control this label.
 * 3. Otherwise, default to the given label or "Next"
 * */
function getNextButtonLabel(params: {
  label?: string;
  stepStatus: StepComputed["status"];
  isLastStep: boolean;
  isReadOnly: boolean;
}) {
  const { label, isLastStep, isReadOnly } = params;
  // When ON the last step and NOT IN a read-only state
  // Then return either the label or "Submit"
  if (isLastStep && !isReadOnly) return label ?? msg("LABEL_SUBMIT");
  // When ON the last step and IN a read-only state
  if (isLastStep && isReadOnly) return label ?? msg("LABEL_CONTINUE_LEARNING");
  // Otherwise, default to the given label or "Next"
  return label ?? msg("LABEL_NEXT");
}
